import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import List from "@material-ui/core/List";
import classNames from "classnames";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import {
  getActiveRiskControl,
  getReferenceDataType,
  getWorkItems,
  isLoading,
} from "../../reducers";
import logger from "../../util/logger";
import ExpandableForm from "../forms/ExpandableForm";
import CreateWorkItemForm, {
  CREATE_WORK_ITEM_FORM_NAME,
} from "../workitem/CreateWorkItemForm";
import WorkItemListItem from "../workitem/WorkItemListItem";
import OpenCloseWorkItemFilter from "../workitem/OpenCloseWorkItemFilter";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginBottom: theme.spacing(2),
    border: "none",
    "&:before": {
      height: 0,
    },
  },
  header: {
    border: "none",
    paddingLeft: 0,
  },
}));

const RiskControlVerificationHistoryCard = ({
  className,
  onCreateVerification,
  loading,
  riskControl,
  riskControlVerifications,
  types,
}) => {
  const classes = useStyles();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filteredVerifications, setFilteredVerifications] = useState([]);

  const handleSubmission = (workItem) => {
    logger.debug(
      "submitting work item of type RiskControlVerification with values",
      workItem
    );

    onCreateVerification(workItem).then(() => setShowCreateForm(false));
  };

  const mergedClassName = classNames(classes.root, className);

  const activeRiskControlVerification = riskControlVerifications.find(
    (v) => v.status !== "CLOSED"
  );

  const initialValues = {
    type: "RISK_CONTROL_VERIFICATION",
    associatedRiskControl: { id: riskControl.id },
    title: `Verification for risk control ${riskControl.name}`,
    owner: riskControl.owner,
  };

  return (
    <Card className={mergedClassName} elevation={0}>
      <CardHeader
        data-cy="verificationTasks"
        title="Verification tasks"
        action={
          !activeRiskControlVerification &&
          !loading && (
            <Tooltip title="Add verification" disableFocusListener>
              <div>
                <IconButton
                  onClick={() => setShowCreateForm(true)}
                  disabled={showCreateForm}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </Tooltip>
          )
        }
      />
      <ExpandableForm
        data-cy="newVerification"
        title="New verification"
        submitButtonText="Save"
        maxWidth="md"
        formComponent={CreateWorkItemForm}
        formName={CREATE_WORK_ITEM_FORM_NAME}
        open={showCreateForm}
        onCancel={() => setShowCreateForm(false)}
        onSubmit={(values) => handleSubmission(values)}
        initialValues={initialValues}
        type="RISK_CONTROL_VERIFICATION"
      />
      <OpenCloseWorkItemFilter
        workItems={riskControlVerifications}
        filteredWorkItemsChange={(filtered) =>
          setFilteredVerifications(filtered)
        }
      />
      {filteredVerifications.length > 0 && !loading && (
        <List dense>
          {filteredVerifications.map((verification) => (
            <WorkItemListItem
              data-cy={verification}
              key={verification.id}
              workItem={verification}
              types={types}
              linkTo={`/risk/controls/${riskControl.id}/verifications/${verification.id}`}
            />
          ))}
        </List>
      )}

      {loading && (
        <CardContent>
          <span>Loading...</span>
        </CardContent>
      )}

      {filteredVerifications.length === 0 && !loading && (
        <CardContent>
          <em data-cy="noVerification">
            There are no verifications for this control
          </em>
        </CardContent>
      )}
    </Card>
  );
};

RiskControlVerificationHistoryCard.propTypes = {
  className: PropTypes.string,
  onCreateVerification: PropTypes.func.isRequired,

  // redux
  loading: PropTypes.bool.isRequired,
  riskControl: PropTypes.object,
  riskControlVerifications: PropTypes.array.isRequired,
  types: PropTypes.object.isRequired,
};

RiskControlVerificationHistoryCard.defaultProps = {
  className: undefined,
  riskControl: null,
};

const mapStateToProps = (state) => ({
  loading: isLoading(state),
  riskControl: getActiveRiskControl(state),
  riskControlVerifications: getWorkItems(state),
  types: getReferenceDataType(state, "WorkItemType"),
});

export default connect(mapStateToProps)(RiskControlVerificationHistoryCard);
